import axios from "axios";
import { API_BASE_URL } from "../../config";
import { authHeaders } from "../utils/session";

/**
 * Services master — the lead sources a website form posts against
 * ("Request Demo", "Contact Us"). A lead carries `service_id`, and internal
 * users are mapped to services so a lead auto-assigns inside the right one.
 *
 * Reading is open to any CMS user; every write is superadmin-only and answers
 * 403 otherwise.
 */

const jsonHeaders = () => ({
  ...authHeaders(),
  "Content-Type": "application/json",
});

/** Turn an API failure into the message the user should actually read. */
const apiError = (err, fallback) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  if (status === 403) return new Error(data?.message || "Only a superadmin can manage services.");
  if (status === 404) return new Error(data?.message || "Service not found.");
  // 422 carries either a flat message or Laravel's per-field errors.
  const firstFieldError = data?.errors?.[Object.keys(data?.errors || {})[0]]?.[0];
  return new Error(firstFieldError || data?.message || data?.error || fallback);
};

const unwrap = (res, fallback) => {
  const body = res.data || {};
  if (body.status && body.status !== 200 && body.status !== true) {
    throw new Error(body.message || fallback);
  }
  return body.data ?? body;
};

/** @param {boolean} activeOnly the Employee form's picker wants active rows only */
export const fetchServices = async (activeOnly = false) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/services`, {
      headers: jsonHeaders(),
      params: activeOnly ? { active: 1 } : {},
    });
    const data = unwrap(res, "Failed to load services");
    return Array.isArray(data) ? data : [];
  } catch (err) {
    throw apiError(err, "Failed to load services");
  }
};

export const fetchService = async (id) => {
  try {
    const res = await axios.get(`${API_BASE_URL}/services/${id}`, { headers: jsonHeaders() });
    return unwrap(res, "Failed to load this service");
  } catch (err) {
    throw apiError(err, "Failed to load this service");
  }
};

/** The backend derives the slug from the name — never send one. */
export const createService = async (payload) => {
  try {
    const res = await axios.post(`${API_BASE_URL}/services`, payload, { headers: jsonHeaders() });
    return unwrap(res, "Failed to create this service");
  } catch (err) {
    throw apiError(err, "Failed to create this service");
  }
};

/* No slug in the body: it is the contract the website forms are wired to, so
   changing it would orphan every existing lead and form pointing at it. */
export const updateService = async (id, payload) => {
  try {
    const res = await axios.put(`${API_BASE_URL}/services/${id}`, payload, { headers: jsonHeaders() });
    return unwrap(res, "Failed to update this service");
  } catch (err) {
    throw apiError(err, "Failed to update this service");
  }
};

export const toggleServiceStatus = async (id) => {
  try {
    const res = await axios.patch(
      `${API_BASE_URL}/services/${id}/toggle-status`,
      {},
      { headers: jsonHeaders() },
    );
    return unwrap(res, "Failed to change the status");
  } catch (err) {
    throw apiError(err, "Failed to change the status");
  }
};

export const deleteService = async (id) => {
  try {
    const res = await axios.delete(`${API_BASE_URL}/services/${id}`, { headers: jsonHeaders() });
    return res.data?.message || "Service deleted";
  } catch (err) {
    throw apiError(err, "Failed to delete this service");
  }
};
